import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined"
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined"
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import SMSOutlinedIcon from "@mui/icons-material/SmsOutlined"
import TrendingupIcon from "@mui/icons-material/TrendingUp"
import LoadingButton from "@mui/lab/LoadingButton"
import Timeline from "@mui/lab/Timeline"
import TimelineConnector from "@mui/lab/TimelineConnector"
import TimelineContent from "@mui/lab/TimelineContent"
import TimelineDot from "@mui/lab/TimelineDot"
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem"
import TimelineSeparator from "@mui/lab/TimelineSeparator"
import Divider from "@mui/material/Divider"
import Paper from "@mui/material/Paper"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { formatDistanceToNow } from "date-fns"
import { compareDesc } from "date-fns"
import { useState } from "react"

import Column, {
  CenterEverythingColumn,
} from "../../../../../common/components/Column"
import {
  BaseGlassMixin,
  GlassMixinVariant2,
} from "../../../../../common/components/GlassMixin"
import Row from "../../../../../common/components/Row"
import { formatCurrency } from "../../../../../common/utils"
import { formatDateTime } from "../../../../../common/utils"
import FeedbackClientReplySection from "../../../components/FeedbackClientReplySection"
import { PriorityCircleIcon } from "../../../components/TaskDialog"
import UpdateCreateEmailCampaignDialog from "../../../components/UpdateCreateEmailCampaignDialog"
import UpdateCreateSMSCampaignDialog from "../../../components/UpdateCreateSMSCampaignDialog"
import { TASK_PRIORITIES } from "../../../tasks/utils"
import {
  TRANSACTION_DIRECTIONS,
  addCRMEventToCandidate,
  commissionRatioDisplay,
} from "../../../utils"
import { EVENT_TYPES, TASK_EVENT_TYPES } from "../../data"
import CallRepresentation from "./CallRepresentation"
import CreateNoteDialog from "./CreateNoteDialog"
import { getEmailEventIcon, isEmailEvent } from "./EmailsTab"
import { FileRepresentation } from "./FilesTab"
import { PerformedBySubtitle } from "./PerformedByAttribution"
import TabColumn from "./TabColumn"
import TabContentColumn from "./TabContentColumn"
import TabHeaderRow from "./TabHeaderRow"
import {
  getBookingRescheduleEventDetail,
  getBookingRescheduleEventTitle,
} from "./bookingRescheduleDisplay"
import {
  getFeedbackEventDetail,
  getFeedbackEventTitle,
} from "./feedbackEventDisplay"

export default function ActivityTimelineTab({
  candidate,
  setCandidate,
  crmEvents,
  meetings = [],
  isLoading = false,
  readOnly = false,
}) {
  const [clickedSMSCampaign, setClickedSMSCampaign] = useState(null)
  const [clickedEmailCampaign, setClickedEmailCampaign] = useState(null)
  const [createNoteDialogOpen, setCreateNoteDialogOpen] = useState(false)

  const meetingEvents = meetings.map((meeting) => ({
    ...meeting,
    type: EVENT_TYPES.meeting,
    created: meeting.start_datetime,
  }))

  const flattenedEvents = [...crmEvents, ...meetingEvents]
    .reduce((events, event) => {
      if (isEmailEvent(event) || event.type === EVENT_TYPES.sms) {
        const transactions = event.transactions ?? []
        if (transactions.length === 0) {
          return [...events, event]
        }
        return events.concat(
          transactions.map((tx) => ({
            ...tx,
            type: event.type,
            ...(isEmailEvent(event)
              ? {
                  performed_by_name: event.performed_by_name,
                  performed_by_role: event.performed_by_role,
                  performed_by_badge: event.performed_by_badge,
                  campaign: event.campaign,
                  subject:
                    tx.subject || event.campaign?.subject || event.subject,
                }
              : {
                  performed_by_name: tx.performed_by_name,
                  performed_by_role: tx.performed_by_role,
                  performed_by_badge: tx.performed_by_badge,
                  text: tx.text,
                  direction: tx.direction,
                }),
          }))
        )
      }
      return [...events, event]
    }, [])
    .sort((e1, e2) => compareDesc(new Date(e1.created), new Date(e2.created)))

  function handleCreateNoteDialogClosed(createdNote) {
    if (createdNote) {
      addCRMEventToCandidate(setCandidate, createdNote, EVENT_TYPES.note)
    }
    setCreateNoteDialogOpen(false)
  }

  return (
    <TabColumn>
      <TabHeaderRow title="Activity Timeline">
        {readOnly ? null : (
          <LoadingButton
            variant="outlined"
            color="inherit"
            loading={isLoading}
            startIcon={<DescriptionOutlinedIcon />}
            onClick={() => setCreateNoteDialogOpen(true)}
          >
            Add Note
          </LoadingButton>
        )}
      </TabHeaderRow>
      <TabContentColumn>
        {isLoading ? (
          <TimelineSkeleton />
        ) : flattenedEvents.length === 0 ? (
          <CenterEverythingColumn>
            <Typography variant="body1" color="text.secondary">
              Nothing to show here.
            </Typography>
          </CenterEverythingColumn>
        ) : (
          <StyledTimeline>
            {flattenedEvents.map((crmEvent, i) => (
              <TimelineItem key={i}>
                <TimelineSeparator>
                  <StyledTimelineDot>
                    <TimelineItemIcon crmEvent={crmEvent} />
                  </StyledTimelineDot>
                  {i < flattenedEvents.length - 1 ? (
                    <StyledTimelineConnector />
                  ) : null}
                </TimelineSeparator>
                <StyledTimelineContent>
                  <Column gap={2} alignItems="flex-start">
                    <Column sx={{ gap: 0.25, alignItems: "flex-start" }}>
                      <Row sx={{ gap: 1, alignItems: "center" }}>
                        <Typography variant="h6" color="colors.gray700">
                          {getEventTitle(crmEvent)}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          fontSize="12px"
                        >
                          {/* email events have date */}
                          {formatDistanceToNow(
                            new Date(crmEvent.date || crmEvent.created)
                          )}{" "}
                          ago
                        </Typography>
                      </Row>
                      <PerformedBySubtitle event={crmEvent} />
                    </Column>
                    <TimelinePresentation
                      crmEvent={crmEvent}
                      candidate={candidate}
                      readOnly={readOnly}
                    />
                  </Column>
                </StyledTimelineContent>
              </TimelineItem>
            ))}
            <UpdateCreateSMSCampaignDialog
              open={!!clickedSMSCampaign}
              onClose={() => setClickedSMSCampaign(null)}
              campaign={clickedSMSCampaign}
              renderedText={clickedSMSCampaign?.rendered_text}
            />
            <UpdateCreateEmailCampaignDialog
              open={!!clickedEmailCampaign}
              onClose={() => setClickedEmailCampaign(null)}
              campaign={clickedEmailCampaign}
              renderedBody={clickedEmailCampaign?.rendered_body}
            />
          </StyledTimeline>
        )}
      </TabContentColumn>
      <CreateNoteDialog
        candidate={candidate}
        open={createNoteDialogOpen}
        onClose={handleCreateNoteDialogClosed}
      />
    </TabColumn>
  )
}

function TimelineSkeleton() {
  const timelineLength = 5
  return (
    <StyledTimeline>
      {Array.from({ length: timelineLength }).map((_, i) => (
        <TimelineItem key={i}>
          <TimelineSeparator>
            <StyledTimelineDot>
              <Skeleton variant="circular" width={20} height={20} />
            </StyledTimelineDot>
            {i < timelineLength - 1 ? <StyledTimelineConnector /> : null}
          </TimelineSeparator>
          <StyledTimelineContent>
            <Column gap={2} alignItems="flex-start">
              <Row sx={{ gap: 1, alignItems: "center" }}>
                <Skeleton variant="text" width={160} height={24} />
                <Skeleton variant="text" width={60} height={18} />
              </Row>
              <Skeleton variant="text" width={240} height={18} />
              <Skeleton
                variant="rectangular"
                width={360}
                height={42}
                sx={{ borderRadius: 1 }}
              />
            </Column>
          </StyledTimelineContent>
        </TimelineItem>
      ))}
    </StyledTimeline>
  )
}

function TimelineItemIcon({ crmEvent }) {
  switch (crmEvent.type) {
    case EVENT_TYPES.call:
      return <PhoneOutlinedIcon />
    case EVENT_TYPES.offer:
      return <LocalOfferOutlinedIcon />
    case EVENT_TYPES.sms:
      return <SMSOutlinedIcon />
    case EVENT_TYPES.fileUpload:
      return <AttachFileOutlinedIcon />
    case EVENT_TYPES.note:
      return <DescriptionOutlinedIcon />
    case EVENT_TYPES.taskEvent:
      return <AssignmentOutlinedIcon />
    case EVENT_TYPES.meeting:
      return <CalendarTodayOutlinedIcon />
    case EVENT_TYPES.feedback:
      return <FeedbackOutlinedIcon />
    case EVENT_TYPES.bookingReschedule:
      return <CalendarTodayOutlinedIcon />
    default:
      return getEmailEventIcon(crmEvent)
  }
}

function getEventTitle(crmEvent) {
  switch (crmEvent.type) {
    case EVENT_TYPES.call:
      return crmEvent.disposition ? "Call attempt" : "Call"
    case EVENT_TYPES.offer:
      return "Proposal sent"
    case EVENT_TYPES.sms:
      return `SMS ${getTransactionDirectionDisplay(crmEvent)}`
    case EVENT_TYPES.fileUpload:
      return "File attached"
    case EVENT_TYPES.note:
      return "Note added"
    case EVENT_TYPES.taskEvent:
      switch (crmEvent.event_type) {
        case TASK_EVENT_TYPES.created:
          return "Task added"
        case TASK_EVENT_TYPES.markedDone:
          return "Task completed"
        default:
          return "Task marked not complete"
      }
    case EVENT_TYPES.meeting:
      return new Date(crmEvent.start_datetime) > new Date()
        ? "Meeting scheduled"
        : "Meeting held"
    case EVENT_TYPES.feedback:
      return getFeedbackEventTitle(crmEvent)
    case EVENT_TYPES.bookingReschedule:
      return getBookingRescheduleEventTitle(crmEvent)
    default:
      return `Email ${getTransactionDirectionDisplay(crmEvent)}`
  }
}

function TimelinePresentation({ crmEvent, candidate, readOnly }) {
  switch (crmEvent.type) {
    case EVENT_TYPES.offer:
      return <OfferRepresentation crmEvent={crmEvent} />
    case EVENT_TYPES.call:
      return <CallRepresentation crmEvent={crmEvent} />
    case EVENT_TYPES.sms:
      return <SMSRepresentation crmEvent={crmEvent} />
    case EVENT_TYPES.email:
    case EVENT_TYPES.directEmail:
    case EVENT_TYPES.emailReceiveEvent:
      return <EmailRepresentation crmEvent={crmEvent} />
    case EVENT_TYPES.fileUpload:
      return (
        <FileRepresentationContainer>
          <FileRepresentation fileEvent={crmEvent} />
        </FileRepresentationContainer>
      )
    case EVENT_TYPES.note:
      return (
        <NoteContainer>
          <Typography
            variant="body1"
            fontSize="14px"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {crmEvent.content}
          </Typography>
        </NoteContainer>
      )
    case EVENT_TYPES.taskEvent:
      return <TaskEventRepresentation crmEvent={crmEvent} />
    case EVENT_TYPES.meeting:
      return (
        <Column gap={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {crmEvent.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(crmEvent.start_datetime)}
          </Typography>
          {crmEvent.is_rescheduled ? (
            <>
              <Typography variant="body2" color="warning.main" fontWeight={600}>
                Rescheduled
              </Typography>
              {crmEvent.reschedule_reason ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  Reason: {crmEvent.reschedule_reason}
                </Typography>
              ) : null}
            </>
          ) : null}
        </Column>
      )
    case EVENT_TYPES.feedback:
      return (
        <FeedbackRepresentation
          crmEvent={crmEvent}
          candidate={candidate}
          readOnly={readOnly}
        />
      )
    case EVENT_TYPES.bookingReschedule:
      return <BookingRescheduleRepresentation crmEvent={crmEvent} />
    default:
      return null
  }
}

function BookingRescheduleRepresentation({ crmEvent }) {
  const { previous, updated, reason } =
    getBookingRescheduleEventDetail(crmEvent)
  return (
    <NoteContainer>
      <Column gap={0.5} alignItems="flex-start">
        {previous && updated ? (
          <Typography variant="body2" fontWeight={600}>
            {previous} → {updated}
          </Typography>
        ) : null}
        {reason ? (
          <Typography
            variant="body1"
            fontSize="14px"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            Reason: {reason}
          </Typography>
        ) : null}
      </Column>
    </NoteContainer>
  )
}

function FeedbackRepresentation({ crmEvent, candidate, readOnly }) {
  const { label, comments } = getFeedbackEventDetail(crmEvent)
  const [clientReplies, setClientReplies] = useState(
    () => crmEvent.client_replies ?? []
  )
  return (
    <NoteContainer>
      <Column gap={0.5} alignItems="flex-start">
        {label ? (
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
        ) : null}
        {comments ? (
          <Typography
            variant="body1"
            fontSize="14px"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {comments}
          </Typography>
        ) : null}
        {!readOnly ? (
          <FeedbackClientReplySection
            feedbackEventId={crmEvent.id}
            feedbackType={crmEvent.feedback}
            feedbackComments={comments}
            candidateName={candidate?.candidate_name}
            clientReplies={clientReplies}
            onRepliesUpdated={setClientReplies}
          />
        ) : null}
      </Column>
    </NoteContainer>
  )
}

function OfferRepresentation({ crmEvent }) {
  return (
    <OfferBox elevation={1}>
      <OfferStat
        title="Signing bonus"
        value={formatCurrency(crmEvent.template.signing_bonus)}
        Icon={TrendingupIcon}
      />
      <Divider orientation="vertical" sx={{ height: "42px" }} />
      <OfferStat
        title="Commission ratio"
        value={commissionRatioDisplay(crmEvent.template.commission_tiers)}
        Icon={BalanceOutlinedIcon}
      />
    </OfferBox>
  )
}

function SMSRepresentation({ crmEvent }) {
  return <Typography variant="body2">{crmEvent.text}</Typography>
}

function EmailRepresentation({ crmEvent }) {
  return <Typography variant="body2">{crmEvent.subject}</Typography>
}

function OfferStat({ title, value, Icon }) {
  return (
    <Row sx={{ alignItems: "center", gap: 1 }}>
      <OfferIcon>
        <Icon />
      </OfferIcon>
      <Column>
        <Typography
          variant="body1"
          color="text.secondary"
          fontSize="12px"
          sx={{ textWrap: "nowrap" }}
        >
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={700}>
          {value}
        </Typography>
      </Column>
    </Row>
  )
}

function TaskEventRepresentation({ crmEvent }) {
  const task = crmEvent.task
  const taskMarkedComplete = crmEvent.event_type === TASK_EVENT_TYPES.markedDone
  return (
    <TaskBox elevation={1}>
      <Column gap={1}>
        <Row sx={{ alignItems: "center", gap: 1 }}>
          {taskMarkedComplete && (
            <CheckCircleOutlinedIcon
              sx={{
                fontSize: "18px",
                color: "success.main",
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            variant="body1"
            fontWeight={600}
            color="text.primary"
            sx={{
              textDecoration: taskMarkedComplete ? "line-through" : "none",
              opacity: taskMarkedComplete ? 0.6 : 1,
            }}
          >
            {task.title}
          </Typography>
        </Row>
        <PerformedBySubtitle event={crmEvent} />
        <Row sx={{ alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary" fontSize="11px">
            Priority:
          </Typography>
          <Row sx={{ gap: 0.75, alignItems: "center" }}>
            <PriorityCircleIcon
              priority={TASK_PRIORITIES[task.priority]}
              sx={{ width: "10px", height: "10px" }}
            />
            <Typography variant="body2" sx={{ fontSize: "11px" }}>
              {TASK_PRIORITIES[task.priority]}
            </Typography>
          </Row>
        </Row>
      </Column>
    </TaskBox>
  )
}

function getTransactionDirectionDisplay(transaction) {
  return transaction.direction === TRANSACTION_DIRECTIONS.incoming
    ? "received"
    : "sent"
}

const StyledTimeline = styled(Timeline)(({ theme }) => ({
  padding: 0,
  margin: 0,
  [`& .${timelineItemClasses.root}:before`]: {
    flex: 0,
    padding: 0,
  },
}))

const StyledTimelineDot = styled((props) => (
  <TimelineDot variant="outlined" {...props} />
))(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "40px",
  height: "40px",
  marginBlock: "4px",
  ...BaseGlassMixin,
  border: "1px solid rgba(255, 255, 255, 0.5)",
  boxShadow:
    "0 20px 25px -5px rgba(245, 218, 255, 0.10),0 8px 10px -6px rgba(52, 64, 84, 0.10)",
  "& .MuiSvgIcon-root": {
    fill: theme.palette.colors.gray700,
    width: "20px",
    height: "20px",
  },
  [theme.breakpoints.only("xs")]: {
    width: "24px",
    height: "24px",
    "& .MuiSvgIcon-root": {
      width: "14px",
      height: "14px",
    },
  },
}))

const StyledTimelineContent = styled(TimelineContent)(({ theme }) => ({
  paddingInline: theme.spacing(1),
}))

const StyledTimelineConnector = styled(TimelineConnector)(({ theme }) => ({
  backgroundColor: theme.palette.colors.gray300,
}))

const OfferBox = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "min-content",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  ...GlassMixinVariant2,
  [theme.breakpoints.only("xs")]: {
    padding: theme.spacing(1),
    gap: theme.spacing(1),
  },
}))

const OfferIcon = styled(Row)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "32px",
  height: "32px",
  borderRadius: "6px",
  backgroundColor: theme.palette.colors.gray50,
}))

const FileRepresentationContainer = styled((props) => (
  <Paper elevation={1} {...props} />
))(({ theme }) => ({
  display: "flex",
  width: "360px",
  padding: theme.spacing(2),
  ...GlassMixinVariant2,
  [theme.breakpoints.only("xs")]: {
    padding: theme.spacing(1),
    width: "100%",
  },
}))

const NoteContainer = styled((props) => <Paper elevation={1} {...props} />)(
  () => ({
    paddingInline: "12px",
    paddingBlock: "10px",
    borderTopLeftRadius: 0,
    ...GlassMixinVariant2,
  })
)

const TaskBox = styled(Paper)(({ theme }) => ({
  display: "flex",
  width: "360px",
  padding: theme.spacing(2),
  ...GlassMixinVariant2,
  [theme.breakpoints.only("xs")]: {
    padding: theme.spacing(1),
    width: "100%",
  },
}))
